<template>
  <v-card class="record-detail">
    <v-container grid-list-md text-xs-left pa-0>
      <v-toolbar dense flat>
        <v-toolbar-title>#{{ order._id }}</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-tooltip bottom v-if="(allowCancelOrder || allowCancelOrderFromKA) && cancelOrderVisible">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" v-on:click="handleClickCancelOrder">
              <SvgIcon height="24" width="24" icon="CancelOrder" />
            </v-btn>
          </template>
          <span>{{ $t('record.cancel_order') }}</span>
        </v-tooltip>

        <v-tooltip bottom v-if="allowReuseOrder && reUseOrderVisible">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" v-on:click="handleShowReuseOrder">
              <SvgIcon height="24" width="24" icon="Redo" />
            </v-btn>
          </template>
          <span>{{ $t('record.reuse.create_order') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" v-on:click="handleReuseOrderToOptimize">
              <SvgIcon height="24" width="24" icon="Reuse" />
            </v-btn>
          </template>
          <span>Sử dụng lại thông tin để tạo lại đơn hàng tối ưu đường đi</span>
        </v-tooltip>
        <v-btn icon :href="billLink" target="_blank">
          <v-icon color="grey darken-3">print</v-icon>
        </v-btn>

        <v-btn icon :href="sharedLink" target="_blank">
          <v-icon color="grey darken-3">share</v-icon>
        </v-btn>

        <v-btn icon @click="closeModal">
          <v-icon color="grey darken-3">close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-layout row wrap pa-4 class="record-detail__body custom-scrollbar">
        <v-flex xs6 class="pr-5">
          <v-card class="record-detail__body__card">
            <OrderStatus
              :order="order"
              :hideMessageChat="false"
              :deliveryType="(serviceDetail && serviceDetail.delivery_type) || ''"
              :porInfo="porInfo"
              @onSubmitSupplierRate="handleUpdateSupplierRate"
            />
          </v-card>

          <v-card class="record-detail__body__card" v-if="callbackLogsVisible">
            <CallbackLogs :order="order" />
          </v-card>
        </v-flex>

        <v-flex xs6>
          <v-card class="record-detail__body__card" v-if="createdByInfo.visible">
            <v-list>
              <v-list-tile avatar class="pa-0">
                <v-list-tile-avatar color="green" :tile="false" size="48" class="mr-2 white--text">
                  <img :src="createdByInfo.avatar" v-img-fallback-text="createdByInfo.name" />
                </v-list-tile-avatar>

                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('create_by') }}</v-list-tile-sub-title>
                  <v-list-tile-title
                    >{{ createdByInfo.name }} • {{ createdByInfo.phone }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-card>

          <v-card class="record-detail__body__card">
            <OrderPath
              :order="order"
              :editable="false"
              :porInfo="porInfo"
              @onClickEditPath="openUpdateOrderModal"
              :orderPathHeader="false"
            />

            <div
              class="info__order-note"
              v-if="order.remarks || (order.images && order.images.length > 0)"
            >
              <OrderNote :note="order.remarks" :images="order.images" />
            </div>
          </v-card>

          <v-card class="record-detail__body__card px-3">
            <FeeDetailV2
              showRequestDetail
              :requests="serviceDetail.requests"
              :totalCOD="totalCOD"
              :sumCODOfFailedStoppoints="sumCODOfFailedStoppoints"
              :estimatedFee="order"
            />
          </v-card>

          <v-card class="record-detail__body__card" v-if="orderItemsVisible">
            <Items :items="order.items" />
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <OrderCancel
      hiddenButton
      :autoBack="webInAppHandler_enable"
      :orderStatus="order.status"
      :orderId="order._id"
      :ownerId="ownerId"
      @onCancelledOrder="handleCanceledOrder"
      @onCancelInvalidStatus="handleCancelInvalidStatus"
      ref="orderCancel"
      v-if="!allowCancelOrder || allowCancelOrderFromKA"
    />

    <OnWheelOrderCancel
      hiddenButton
      :autoBack="webInAppHandler_enable"
      :orderStatus="order.status"
      :orderId="order._id"
      :ownerId="ownerId"
      @onCancelledOrder="handleCanceledOrder"
      @onCancelInvalidStatus="handleCancelInvalidStatus"
      ref="orderCancel"
      v-if="allowCancelOrder"
    />
  </v-card>
</template>

<script>
import i18next from '@/plugins/i18next'
import NS_recordDetail_vi from '@/locales2/vi/recordDetail.json'
import NS_recordDetail_en from '@/locales2/en/recordDetail.json'

i18next.addResourceBundle('vi', 'common', NS_recordDetail_vi, true)
i18next.addResourceBundle('en', 'common', NS_recordDetail_en, true)

import { mapState, mapGetters } from 'vuex'
const { includes, get } = require('@/plugins/lodash')
import OrderStatus from '@/components/record_detail_v2/OrderStatus.vue'
import OrderPath from '@/components/record_detail_v2/OrderPath.vue'
import FeeDetailV2 from '@/components/FeeDetailV2.vue'

const OrderNote = () => import('@/components/record_detail_v2/OrderNote')
const Items = () => import('./Items')
const CallbackLogs = () => import('./CallbackLogs')

import record from '@/mixins/record'
import orderService from '@/mixins/order-service'
import PreventScrollMixin from '@/mixins/prevent-scroll'

import { getOrderDetail as apiGetOrderDetail } from '@/store/api/order'

import { getMaxStopPointByService } from '@/plugins/util'
// Auto check to get order detail
const GET_ORDER_DETAIL_DURATION = 10000

const OrderCancel = () => import('@/components/record_detail_v2/OrderCancel.vue')
const OnWheelOrderCancel = () => import('@/onwheel/components/record_detail/OrderCancel.vue')

export default {
  components: {
    OrderStatus,
    OrderPath,
    OrderNote,
    FeeDetailV2,
    Items,
    CallbackLogs,
    OrderCancel,
    OnWheelOrderCancel
  },
  mixins: [record, orderService, PreventScrollMixin],
  props: {
    data: {}
  },
  data() {
    const originHost = (window && window.location && window.location.origin) || './'
    return {
      sharedLink: `${process.env.VUE_APP_CLOUD_BASE_URL}/share-order/${this.data._id}/${this.data.user_id}`,
      billLink: `${originHost}/bill?order_id=${this.data._id}&user_id=${this.data.user_id}`,
      orderId: '',
      ownerId: '',
      order: this.data,
      reuseOrderVisible: false,
      serviceDetail: this.data.service,
      isWaitingReload: false,
      orderLoading: false,
      checkOrderStatusInterval: null,
      loadOrderErrorMessage: undefined,
      isOpenUpdateOrderModal: false,
      isPartnerUser: false,
      ordersTimelineEnable: false
    }
  },
  computed: {
    ...mapState({
      isFocusTab: (state) => state.isFocusTab,
      userToken: (state) => state.user.token,
      userProfile: (state) => state.user.profile,
      orderStatus: (state) => state.recordDetail.order_status,
      supplierLocation: (state) => state.supplierLocation.supplierLocation,
      invalidOrderModeToGetSupplierLocation: (state) => state.supplierLocation.invalidOrderMode,
      webInAppConfig: (state) => state.webInAppConfig
    }),
    ...mapGetters(['isSelectedNationwideService']),
    webInAppHandler_enable() {
      return this.webInAppConfig && this.webInAppConfig.id ? true : false
    },
    orderTags() {
      return (this.userProfile && this.userProfile.extra && this.userProfile.extra.order_tags) || []
    },
    updateOrderVisible() {
      return (
        includes(
          [
            this.$constants.ORDER_STATUS.PAYING,
            this.$constants.ORDER_STATUS.ASSIGNING,
            this.$constants.ORDER_STATUS.IDLE,
            this.$constants.ORDER_STATUS.ACCEPTED
          ],
          this.order.status
        ) && !this.isPartnerPage
      )
      // return false
    },
    thermoBagSurveyVisible() {
      return (
        this.order &&
        this.order.status === this.$constants.ORDER_STATUS.IN_PROCESS &&
        this.order.uniform_verify &&
        this.order.uniform_user_feedback === 2
      )
    },
    thermoBagSurveyMessage() {
      let message = ''
      if (this.thermoBagSurveyVisible) {
        const locale = this.$store.getters.locale
        message = this.order.uniform_verify_message_vi_vn
        if (locale && locale === this.$constants.LOCATE_LANGUAGE.EN.locate) {
          message = this.order.uniform_verify_message_en_us
        }
      }

      return message || this.$t('thermo_bag_survey.des')
    },
    cancelOrderVisible() {
      const cancelTimeout = this.serviceDetail && this.serviceDetail.cancel_timeout
      if (cancelTimeout > 0) {
        const currentTime = new Date().getTime() / 1000
        if (currentTime - this.order.create_time > cancelTimeout) {
          return
        }
      }

      return (
        this.order.status === this.$constants.ORDER_STATUS.ASSIGNING ||
        this.order.status === this.$constants.ORDER_STATUS.ACCEPTED ||
        this.order.status === this.$constants.ORDER_STATUS.IDLE ||
        this.order.status === this.$constants.ORDER_STATUS.PAYING
      )
    },
    reUseOrderVisible() {
      console.log('reUseOrderVisible - this.order: ', this.order)
      const path = get(this.order, 'path') || []
      const omsVersion2 = path.some((stop) => get(stop, 'partner_fields.oms_version') === 'v2')
      console.log('reUseOrderVisible - omsVersion2: ', omsVersion2)
      if (omsVersion2) return !omsVersion2
      const service_id = get(this.order, 'service_id') || ''
      let isPartner = service_id.includes('-PARTNER-')
      return (
        !isPartner &&
        (this.order.status === this.$constants.ORDER_STATUS.CANCELLED ||
          this.order.status === this.$constants.ORDER_STATUS.FAILED)
      )
    },
    orderActivePathId() {
      return this.$store.getters.orderActivePath.id
    },
    orderIdParam() {
      return this.$route.params.id
    },
    isPartnerPage() {
      return this.$_PAGE === this.$constants.PAGE.PARTNER
    },
    porInfo() {
      if (
        this.order &&
        this.order.path.length > 0 &&
        (this.order.path[0].return_time || this.order.return_time)
      ) {
        const regex = /(http)?s?:?(\/\/[^"']*\.(?:png|jpg|jpeg|gif|png|svg))/g
        if (regex.test(this.order.path[0].por_info)) {
          return this.order.path[0].por_info
        }
      }

      return ''
    },
    totalCOD() {
      let totalCOD = 0

      if (this.order && this.order.path.length > 0) {
        this.order.path.forEach((p) => {
          totalCOD += p.cod || 0
        })
      }

      return totalCOD
    },
    sumCODOfFailedStoppoints() {
      let sumCODOfFailedStoppoints = 0
      if (
        this.order &&
        this.order.status === this.$constants.ORDER_STATUS.COMPLETED &&
        this.order.path.length > 0
      ) {
        this.order.path.forEach((p) => {
          if (p.status === this.$constants.ORDER_STATUS.FAILED) {
            sumCODOfFailedStoppoints += p.cod || 0
          }
        })
      }
      return sumCODOfFailedStoppoints
    },
    allowPickupIndicator() {
      return this.order && this.order.status === this.$constants.ORDER_STATUS.ASSIGNING
    },

    nationwideTrackingLink() {
      let link = this.order && this.order.tracking_link
      if (link) {
        link = link.replace('http://', 'https://')
      }
      return link
    },
    createdByInfo() {
      const userProfile = this.$store.getters.userProfile || {}
      const createdBy = this.order && this.order.user_id
      const visible = userProfile && createdBy && userProfile._id !== createdBy
      let result = {
        visible
      }

      if (visible) {
        result.avatar = this.order.user_avatar
        result.name = this.order.user_name
        result.phone = createdBy
      }

      return result
    },
    orderItemsVisible() {
      return this.order.items && this.order.items.length > 0
    },
    callbackLogsVisible() {
      return this.order.callback_logs && this.order.callback_logs.length > 0
    },
    allowCancelOrder() {
      return (
        this.userProfile &&
        this.userProfile.partner &&
        [
          this.$constants.HARDCODED_PARTNER.BIGC,
          this.$constants.HARDCODED_PARTNER.HOMEFARM,
          this.$constants.HARDCODED_PARTNER.COOP_MART
        ].includes(this.userProfile.partner.toLowerCase())
      )
    },
    allowCancelOrderFromKA() {
      const kaProfile = this.$store.getters.kaProfile
      return get(kaProfile, 'ro_settings.cancel_order_ahamove') || false
    },
    allowReuseOrder() {
      return (
        this.userProfile &&
        this.userProfile.partner &&
        [
          this.$constants.HARDCODED_PARTNER.BIGC,
          this.$constants.HARDCODED_PARTNER.COOP_MART
        ].includes(this.userProfile.partner.toLowerCase())
      )
    },
    kaProfile: {
      cache: false,
      get() {
        return this.$store.getters.kaProfile
      }
    }
  },
  methods: {
    handleReuseOrderToOptimize() {
      if (!this.kaProfile) return

      const features = this.kaProfile.features_v2 || this.kaProfile.features

      if (!features) return

      const hasRoFeature = features.some((feat) => feat.id === 'route-optimization')

      if (hasRoFeature) {
        localStorage.setItem(
          this.$constants.LOCALSTORAGE_REUSE_ORDER_DATA,
          JSON.stringify(this.order)
        )
        this.$router.push({
          path: `/feature`,
          query: { p: 'route-optimization', lr: this.order._id }
        })
      }
    },
    initial() {},
    getOrderById() {
      return new Promise((resolve, reject) => {
        const token = this.userToken || window.token
        if (!token) {
          reject()
          this.$store.dispatch('setSigninFallback', {
            path: this.$route.path,
            query: this.$route.query
          })
          this.$router.push({ name: 'sign-in' })
        }

        let params = {
          format: 'json',
          order_id: this.orderId
        }
        if (this.isPartnerPage) {
          params.user_id = this.ownerId
        }

        apiGetOrderDetail(params)
          .then(resolve)
          .catch((response) => {
            switch (response.code) {
              case this.$constants.ERROR_CODES.NOT_AUTHORIZED.code:
                this.loadOrderErrorMessage = this.$t('record.error.not_authorized')
                this.showErrorMessage(this.loadOrderErrorMessage)
                break
              default:
                this.loadOrderErrorMessage = this.$t('record.error.default')
                this.$_globalMixin_requestErrorHandle(response)
            }

            reject()
          })
      })
    },
    getOrderDetail() {
      this.orderLoading = true
      this.getOrderById()
        .then((order) => {
          this.orderLoading = false
          if (order) {
            this.updateOrder(order)
            this.order = order
            this.$store.dispatch('setOrderId', order._id)
            this.$store.dispatch('setOrderStatus', order.status)
            this.initGetOrderDetailInterval()
            this.trackingSupplier(order.status)
          }
        })
        .catch(() => {
          this.orderLoading = false
        })
    },
    updateOrder(order) {
      const service = order.service
      order.service_name = service['name' + this.$store.getters.localeSuffix] || service.name
      order.supplier_avatar = order.supplier_avatar
        ? order.supplier_avatar
        : '/assets/images/supplier_avatar.png'
      this.$_record_updateOrderGeneral(order, service)
      this.setActionsVisible()
      if (!this.serviceDetail) {
        this.serviceDetail = {}
        this.$_record_getOrderServiceDetail(order.service._id).then((service) => {
          if (service) {
            this.serviceDetail = Object.assign({}, service, order.service)
          }
        })
      }
    },
    setActionsVisible() {
      this.reuseOrderVisible = true
    },
    reUpdateOrderDetail(newOrder) {
      this.updateOrder(newOrder)
      this.order = Object.assign({}, newOrder)

      if (
        this.order.status === this.$constants.ORDER_STATUS.COMPLETED &&
        (!this.order.rating_by_user || this.order.rating_by_user === 0)
      ) {
        this.externalFunc_checkCompletedOrders()
      }

      if (
        (this.order.status === this.$constants.ORDER_STATUS.COMPLETED ||
          this.order.status === this.$constants.ORDER_STATUS.CANCELLED) &&
        this.checkOrderStatusInterval
      ) {
        clearInterval(this.checkOrderStatusInterval)
        this.$store.dispatch('stopTrackingSupplierLocation')
        this.$store.dispatch('stopTrackingSupplierEta')
      }

      // Update order data in record history to update order in list to correct status
      this.$store.dispatch('updateOrder', this.order)
    },
    reloadOrderDetail() {
      if (this.$store.state.isFocusTab) {
        this.getOrderById()
          .then((newOrder) => {
            if (newOrder) {
              this.trackingSupplier(newOrder.status)
              this.reUpdateOrderDetail(newOrder)
            }
          })
          .catch(() => {})
      } else {
        // Waiting for focus on Tab
        this.isWaitingReload = true
      }
    },
    handleCancelOrder(order) {
      if (order) {
        // Invalid order's status
        this.reUpdateOrderDetail(order)
      } else {
        // 2 cases:
        // - Cancel order success
        // - Cannot check order detail before cancel
        this.reloadOrderDetail()
      }
    },
    reUseOrder() {
      const orderStopPoints = this.order.path.length - 1
      const serviceMaxStoppoint = getMaxStopPointByService(this.serviceDetail, this.userProfile)
      if (serviceMaxStoppoint < orderStopPoints) {
        return this.showErrorMessage('Cannot re-use this order')
      }

      let newState = Object.assign(
        {},
        this.$_record_buildNewStateOrderForm(this.order, this.serviceDetail)
      )

      if (this.$_PAGE === this.$constants.PAGE.TRUCK) {
        this.$store.dispatch('reuseStateToCreateOrderTruck', newState)
        this.$router.push({
          name: 'new_order'
        })
      } else {
        this.$store.dispatch('resetOrderForm').then(() => {
          this.$store.dispatch('setOrderFormAllInfo', newState).then(() => {
            this.$router.push({
              name: 'new_order',
              params: {
                allowEstimateFeeWhenMounted: true
              }
            })
          })
        })

        this.$analyticTracking.logEvent(
          this.$trackingEvents.ACTIONS.CLICK,
          this.$trackingEvents.EVENTS.ORDER_DETAIL_REUSE_BTN,
          {
            order: this.$analyticTracking.getOrderTrackingData(this.order)
          }
        )
      }
    },
    showErrorMessage(message) {
      const error = {
        visible: true,
        title: message,
        color: 'error',
        timeout: 1500
      }
      this.$store.dispatch('setSnackbar', error)
    },
    handleUpdateSupplierRate(newValue) {
      this.order = {
        ...this.order,
        rating_by_user: newValue.rating,
        comment_by_user: newValue.comment,
        badge_ids: newValue.badge_ids || ''
      }
    },
    isUpdatedPath(newPath, oldPath) {
      for (let index in newPath) {
        if (newPath[index].status !== oldPath[index].status) return true
      }
      return false
    },
    initGetOrderDetailInterval() {
      if (
        this.order.status === this.$constants.ORDER_STATUS.COMPLETED ||
        this.order.status === this.$constants.ORDER_STATUS.CANCELLED
      ) {
        return
      }

      const intervalDuration =
        (this.webInAppHandler_enable && this.webInAppConfig.getOrderDetailDuration) ||
        GET_ORDER_DETAIL_DURATION

      this.checkOrderStatusInterval = setInterval(async () => {
        if (this.isFocusTab) {
          if (
            // Invalid order mode (Completed/...)
            this.invalidOrderModeToGetSupplierLocation ||
            // Order status change
            (this.supplierLocation && this.supplierLocation.order_status !== this.order.status)
          ) {
            this.$store.dispatch('updateInvalidOrderMode', false)
            this.reloadOrderDetail()
          }
          // auto reload for status !== ACCEPTED/IN_PROCESS
          else if (
            this.order.status !== this.$constants.ORDER_STATUS.ACCEPTED &&
            this.order.status !== this.$constants.ORDER_STATUS.IN_PROCESS
          ) {
            let params = {
              format: 'json',
              order_id: this.orderId
            }
            if (this.isPartnerPage) {
              params.user_id = this.ownerId
            }

            apiGetOrderDetail(params)
              .then((data) => {
                if (
                  (data && data.status !== this.order.status) ||
                  this.isUpdatedPath(data.path, this.order.path)
                ) {
                  this.reUpdateOrderDetail(data)
                  this.$store.dispatch('setOrderStatus', this.order.status)
                }
              })
              .catch(() => {})
          }
        }
      }, intervalDuration)
    },

    // for wootric survey - check task PT-537
    externalFunc_checkCompletedOrders() {
      const cookieName = 'aha_survey'
      const checked = this.$cookie.get(cookieName)
      if (!checked) {
        const count = 15
        const nextDays = 90
        const params = Object.assign({
          offset: 0,
          count: count + 1,
          timeout: this.$constants.HTTP_REQUEST.TIMEOUT,
          token: this.userToken,
          fields: ['_id'],
          status: this.$constants.ORDER_STATUS.COMPLETED
        })
        this.$_globalMixin_loadOrdersList(params)
          .then((data) => {
            if (data.length >= count) {
              const nextTime = nextDays * 24 * 60 * 60 * 1000
              const surveyCookie = `${new Date().getTime()}-waiting-${
                new Date().getTime() + nextTime
              }`

              this.$cookie.set(cookieName, surveyCookie, { expires: 365 })
            }
            this.$analyticTracking.checkShowSurvey()
          })
          .catch(() => {})
      } else {
        this.$analyticTracking.checkShowSurvey()
      }
    },
    onCompleteSurvey(value) {
      this.order.uniform_user_feedback = value
    },
    openUpdateOrderModal() {
      this.isOpenUpdateOrderModal = true
      this.$analyticTracking.logEvent(
        this.$trackingEvents.ACTIONS.CLICK,
        this.$trackingEvents.EVENTS.ORDER_DETAIL_UPDATE_BTN,
        { order: this.order }
      )
    },
    onCloseUpdateModal(reload, newPath) {
      if (reload) {
        this.getOrderById()
          .then((newOrder) => {
            if (newOrder) {
              this.reUpdateOrderDetail(newOrder)
            }
          })
          .catch(() => {})
      } else if (newPath) {
        this.order.path = newPath
      } else {
        this.isOpenUpdateOrderModal = false
      }
    },
    // Get tracking location of supplier
    trackingSupplierLocation(orderStatus) {
      const trackingStatus = [
        this.$constants.ORDER_STATUS.ACCEPTED,
        this.$constants.ORDER_STATUS.IN_PROCESS
      ]
      if (trackingStatus.indexOf(orderStatus) > -1) {
        if (!this.$store.getters.trackingSupplierLocationInterval) {
          const trackingUrl = `${process.env.VUE_APP_API_URL}${this.$constants.API.order.locate_supplier}?order_id=${this.order._id}&user_id=${this.order.user_id}`
          this.$store.dispatch('initTrackingSupplierLocation', {
            url: trackingUrl
          })
        }
      } else {
        this.$store.dispatch('stopTrackingSupplierLocation')
      }
    },
    estimatedSupplierTimeArrival(orderStatus) {
      // ACCEPTED -> picking up time
      // IN_PROCESS -> delivery time

      const allowETAStatusAccepted = orderStatus === this.$constants.ORDER_STATUS.ACCEPTED
      const allowETAStatusInprocess =
        orderStatus === this.$constants.ORDER_STATUS.IN_PROCESS && this.order.path.length === 2

      if (allowETAStatusAccepted || allowETAStatusInprocess) {
        this.$store.dispatch('setSupplierETA', undefined)
        this.$store.dispatch('stopTrackingSupplierEta')

        const pointToEstimated = allowETAStatusAccepted ? this.order.path[0] : this.order.path[1]
        this.$store.dispatch('initTrackingSupplierEta', pointToEstimated)
      } else {
        this.$store.dispatch('stopTrackingSupplierEta')
      }
    },
    trackingSupplier(orderStatus) {
      this.trackingSupplierLocation(orderStatus)
      this.estimatedSupplierTimeArrival(orderStatus)
    },
    // End get tracking location of supplier

    handleChangeTags(tags) {
      this.order.tags = [...tags]
      this.$store.dispatch('resetOrders')
      this.$forceUpdate()
    },
    showOrderTimeline() {
      this.ordersTimelineEnable = true
    },
    redirectToPartnerPage() {
      window.open(process.env.VUE_APP_PARTNER_URL, '_blank')
    },
    handleClickCancelOrder() {
      if (this.$refs.orderCancel) {
        this.$refs.orderCancel.showDialog = true
      }
    },
    closeModal() {
      this.$store.dispatch('setActiveOrder', null)
    },
    handleBack() {
      if (this.webInAppHandler_enable) {
        const query = {}

        if (this.$route.query.webinapp && this.$route.query.redirectUrl) {
          return window.open(decodeURIComponent(this.$route.query.redirectUrl), '_self')
        } else if (this.$route.query.webinapp && this.$route.query.wallet_id) {
          query.webinapp = this.$route.query.webinapp
          query.wallet_id = this.$route.query.wallet_id
        }
        this.$router.push({ path: '/', query })
      } else {
        this.$router.push({ name: 'records' })
      }
    },
    handleCanceledOrder() {
      if (this.webInAppHandler_enable) {
        this.handleBack()
      }
      this.$emit('onCancelOrder')
    },
    handleCancelInvalidStatus(order) {
      this.$emit('onCancelOrder', order)
    },
    handleShowReuseOrder() {
      this.$emit('onShowReuseOrder')
    }
  },
  watch: {
    // orderStatus on store update by this component or offline-handler plugin
    orderStatus(newVal) {
      this.trackingSupplier(newVal)
      if (this.order && this.order.status !== newVal) {
        this.reloadOrderDetail()
      }
    },
    orderActivePathId(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.isFocusTab) {
          this.getOrderById()
            .then((order) => {
              if (order) {
                this.$_record_updateOrderGeneral(order, order.service)
                this.order.path = order.path
              }
            })
            .catch(() => {})
        } else {
          this.isWaitingReload = true
        }
      }
    },
    isFocusTab(newVal, oldVal) {
      if (newVal !== oldVal && newVal && this.isWaitingReload) {
        this.isWaitingReload = false
        this.getOrderById()
          .then((newOrder) => {
            if (newOrder) {
              this.updateOrder(newOrder)
              this.order = newOrder
            }
          })
          .catch(() => {})
      }
    },
    orderIdParam(newVal, oldVal) {
      if (oldVal && newVal && oldVal !== newVal) {
        this.initial()
      }
    }
  },
  async created() {
    this.initial()
  },
  mounted() {
    if (this.isMobile) {
      this.$_preventScroll(true)
    }
  },
  beforeDestroy() {
    if (this.isMobile) {
      this.$_preventScroll(false)
    }

    this.$store.dispatch('setActiveOrder', null)

    // this.$store.dispatch("setOrderId", null);
    // this.$store.dispatch("setOrderStatus", undefined);
    // this.$store.dispatch("stopTrackingSupplierLocation");
    // this.$store.dispatch("stopTrackingSupplierEta");
    // this.$store.dispatch("updateInvalidOrderMode", false);
    // if (this.checkOrderStatusInterval)
    //   clearInterval(this.checkOrderStatusInterval);
  }
}
</script>
<style lang="scss" scoped>
$form_width: 520px;
$form_header_height: 132px;

.record-detail__body {
  height: calc(100vh - 200px);
  overflow-x: hidden;
  overflow-y: scroll;
  margin: 0 !important;

  .record-detail__body__card {
    box-shadow: none;
    border: 1px solid #eee;
    margin-bottom: 1.5rem;
  }
}

.order-detail {
  height: 100vh;
  display: flex;

  // Prevent top/bottom bar of browser
  &.order-detail-mobile {
    height: stretch;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;

    .order-detail__info {
      width: 100%;
    }
  }

  .order-detail__info {
    // width: $form_width;
    background-color: #f2f4f7;
    box-shadow: 4px 0 6px rgba(0, 0, 0, 0.15);
    z-index: 1;

    .order-detail__info__header {
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.05);
    }

    .order-detail__info__body {
      height: calc(100% - #{$form_header_height});
      overflow-y: auto;
      padding: 12px;

      .info__order-status,
      .info__order-path,
      .info__order-fee,
      .info__order-tags,
      .info__order-note,
      .info__order-created-by,
      .info__order-cancel-webinapp {
        margin-bottom: 12px;
        border-radius: 8px;
        background-color: #fff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
      }

      .info__order-cancel-webinapp {
        height: 40px;
        color: #e8313b;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .info__order-fee {
        padding: 16px;
      }

      .info__order-map-mobile {
        height: 360px;
        width: calc(100% + 20px);
        margin-left: -10px;
        margin-top: -11px;
      }

      .info__order-scrollable {
        width: 32px;
        height: 4px;
        background: #bcbcbc;
        border-radius: 19px;
        margin: 0 auto 8px;
      }

      .info__order-created-by {
        overflow: hidden;
        padding: 16px;

        .v-list {
          padding: 0;
          margin: 0;

          .v-list__tile {
            padding: 0;
          }

          .v-list__tile__sub-title {
            color: #7a8189;
            font-size: 14px;
          }

          .v-list__tile__title {
            color: #1a1a1a;
            font-weight: 500;
            font-size: 14px;
          }
        }
      }
    }
  }

  /* .order-detail__map-desktop {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  } */

  .order-detail__timeline {
    position: fixed;
    z-index: 4;
    top: 20px;
    right: 20px;
    margin: 0;

    .v-btn {
      height: 40px;
      width: 40px;
      margin: 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42) !important;
      color: #7a8189;
    }

    .order-detail__timeline__icon {
      margin-left: 4px;
    }

    .order-detail__timeline {
      position: fixed;
      z-index: 4;
      top: 20px;
      right: 20px;
      margin: 0;

      .v-btn {
        height: 40px;
        width: 40px;
        margin: 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42) !important;
        color: #7a8189;
      }

      .order-detail__timeline__icon {
        margin-left: 4px;
      }
    }

    .order-detail__partner {
      position: fixed;
      z-index: 4;
      top: 72px;
      right: 20px;
      height: 40px;
      width: 40px;
      margin: 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42) !important;
      color: #7a8189;
    }
  }

  .order-detail__partner {
    position: fixed;
    z-index: 4;
    top: 72px;
    right: 20px;
    height: 40px;
    width: 40px;
    margin: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42) !important;
    color: #7a8189;
  }
}
</style>

<style lang="scss">
.order-detail {
  .order-detail__map-desktop {
    flex: 1;
    height: 100%;
    z-index: 0;

    > div {
      height: 100%;

      #trackingMap {
        height: 100%;
      }
    }

    #trackingMap {
      .leaflet-control-zoom {
        margin-right: 20px !important;
        margin-top: 72px !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42);
        border-radius: 4px;
        border: none !important;

        .leaflet-control-zoom-in {
          border-radius: 4px 4px 0 0 !important;
        }

        .leaflet-control-zoom-out {
          border-radius: 0 0 4px 4px !important;
        }

        .leaflet-control-zoom-in,
        .leaflet-control-zoom-out {
          width: 40px !important;
          height: 32px !important;
          font-size: 18px;
          color: #7a8189;
        }
      }
    }

    &.partner {
      #trackingMap {
        .leaflet-control-zoom {
          margin-right: 20px !important;
          margin-top: 72px !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42);
          border-radius: 4px;
          border: none !important;

          .leaflet-control-zoom-in {
            border-radius: 4px 4px 0 0 !important;
          }

          .leaflet-control-zoom-out {
            border-radius: 0 0 4px 4px !important;
          }

          .leaflet-control-zoom-in,
          .leaflet-control-zoom-out {
            width: 40px !important;
            height: 32px !important;
            font-size: 18px;
            color: #7a8189;
          }
        }
      }

      &.partner {
        #trackingMap {
          .leaflet-control-zoom {
            margin-right: 20px !important;
            margin-top: 124px !important;
          }
        }
      }
    }
  }

  .info__order-created-by {
    .v-list__tile {
      padding: 0;
    }
  }
}

.order-detail__timeline-list {
  height: 70vh;
  width: 400px;
  position: relative;
}
</style>
